import { stat } from "fs";
import { map } from "jquery";
import { v4 as uuidv4 } from "uuid";
// ==========================================
// tree-mapper.ts
// ==========================================

/**
 * Mapper: Backend Payload → TreeNodeModel
 * Genera estructura jerárquica:
 * CLIENT
 *  └── CORP_CONTRACT
 *       └── DIV_CONTRACT
 *            └── STORE_CONTRACT
 *                 └── DEVICE_CONTRACT
 */

// ==========================================
// Tipos base
// ==========================================

export type EntityType =
    | "CLIENT"
    | "CORP_CONTRACT"
    | "DIV_CONTRACT"
    | "STORE_CONTRACT"
    | "DEVICE_CONTRACT";

export interface TreeNodeModel {
    id: string;
    type: EntityType;
    name: string;
    saved: boolean;
    children: TreeNodeModel[];
    entityId?: string;
    entityData?: any;
    isExistent?: boolean;
    isModified?: boolean;
}

// ==========================================
// PUBLIC METHOD
// ==========================================

export const mapPayloadToTree = (response: any): TreeNodeModel => {
    const { payload } = response;
    const { client, divisions } = payload;

    return {
        id: generateId(),
        type: "CLIENT",
        name: client.companyName,
        saved: true,
        isExistent: true,
        isModified: false,
        entityId: client.clientNumber,
        entityData: mapClientEntity(client),
        children: divisions?.map((d: any) =>
            mapCorpContract(d, client)
        ) ?? []
    };
};

// ==========================================
// CLIENT
// ==========================================

const mapClientEntity = (client: any) => ({
    id: "",
    clientType: "MERCHANT_RESIDENT",
    clientId: client.clientId,
    registrationType: client.registrationType,
    companyName: client.companyName,
    contractNumber: client.contractNumber,
    tradeName: client.shortName,
    shortName: client.shortName,
    legalRep: {
        firstName: client.firstName,
        lastName: client.lastName
    },
    merchantNumber: client.clientNumber,
    registrationNumber: client.registrationNumber,
    taxpayerId: "",
    branch: client.branch?.toString().padStart(7, "0"),
    reason: client.registrationDetails,
    status: "DRAFT",
    createdAt: new Date()
});

// ==========================================
// CORP CONTRACT
// ==========================================

const mapCorpContract = (
    division: any,
    client: any
): TreeNodeModel => ({
    id: generateId(),
    type: "CORP_CONTRACT",
    name: client.contractName,
    isExistent: true,
    isModified: false,
    saved: true,
    children: [mapDivision(division)],
    entityData: {
        id: "",
        clientId: "",
        product: "052-Acq Corporation level",
        contractName: client.contractName,
        contractNumber: client.contractNumber,
        mcc: "",
        registrationAddress: mapRegistrationAddress(client),
        classifiers: client.classifiers,
        status: "DRAFT"
    }
});

// ==========================================
// DIV CONTRACT
// ==========================================
const getParam = (params, code) =>
    params?.find(p => p.code === code)?.value;



const mapDivision = (division: any): TreeNodeModel => {

    let payfacData = {};

    const storeWithDynamic = division.stores?.find(store =>
        getParam(store.contractParameters, "DYNAMIC_MERCH") === "Y"
    );

    if (storeWithDynamic) {
        payfacData = {
            egwPaymentFacilitator: "Y",
            payfacMastercardId: getParam(storeWithDynamic.contractParameters, "MDE4837_1"),
            payfacVisaId: getParam(storeWithDynamic.contractParameters, "VF104_2_56_1")
        };
    }

    return {
        id: generateId(),
        type: "DIV_CONTRACT",
        name: division.contractName,
        isExistent: true,
        isModified: false,
        saved: true,
        entityData: {
            contractNumber: division.contractNumber,
            ...payfacData
        },
        children: division.stores.length > 10 ? [] : division.stores?.map(mapStore) ?? []
    };
};

// ==========================================
// STORE CONTRACT
// ==========================================

const mapStore = (store: any): TreeNodeModel => ({
    id: generateId(),
    type: "STORE_CONTRACT",
    name: store.contractName,
    saved: true,
    isExistent: true,
    isModified: false,
    children: store.devices?.map(mapDevice) ?? [],
    entityData: {
        id: "",
        divisionContractId: "",
        product: "052-Acq Store level",
        contractName: store.contractName,
        contractNumber: store.contractNumber,
        statusCode: store.statusCode,
        mcc: store.mcc,
        merchantId: store.merchantId,
        paymentAddress: store.address ? mapStoreAddress(store) : {},
        classifiers: mapStoreClassifiers(store.classifiers),
        adjustLimits: store.limits.length > 0 ? "Y" : "N",
        storeLimits: store.limits ? mapStoreLimits(store.limits) : [],
        status: "DRAFT"
    }
});

// ==========================================
// DEVICE CONTRACT
// ==========================================

const mapDevice = (device: any): TreeNodeModel => ({
    id: generateId(),
    type: "DEVICE_CONTRACT",
    name: device.contractName,
    saved: true,
    isExistent: true,
    isModified: false,
    children: [],
    entityData: {
        product: getProduct(device.productCode1, device.deviceType),
        contractName: device.contractName,
        terminalId: device.terminalId,
        deviceCategory: device.productCode1 == "ACQ_V1_POS" ? "POS" : "ECOMM",
        deviceType: getDeviceType(device.deviceType),
        deviceStatus: device.deviceStatus,
        statusCode: device.deviceStatus === "500" ? "114" : device.statusCode,
        timeOffset: device.timeOffset,
        macStatus: device.macStatus,
        pbtStatus: device.pbtStatus,
        deviceClassifiers: device.classifiers ? mapDeviceClassifiers(device.classifiers) : [],
        additionalParams: device.deviceParameters ? mapDeviceParams(device.deviceParameters) : [],
        settlementParams: device.contractParameters ? mapSettlementParams(device.contractParameters) : [],
        adjustLimits: device.limits && device.limits.length > 0 ? "Y" : "N",
        deviceLimits: device.limits ? mapDeviceLimits(device.limits) : [],
        adjustTariffs: device.tariffs && device.tariffs.length > 0 ? "Y" : "N",
        deviceTariffs: device.tariffs ? mapDeviceTariffs(device.tariffs) : []
    }
});

const getDeviceType = (deviceType: string): string => {

    if (deviceType.toUpperCase().includes("HOTEL")) {
        return "POS-HOTEL"
    }

    if (deviceType.toUpperCase().includes("ECOMM")) {
        return "ECOMM"
    }

    if (deviceType.toUpperCase().includes("PAYCORE_TOP")) {
        return "Device H2H"
    }

    return "PC-POS";
}

const getProduct = (productCode: string, deviceType: string): string => {

    //device.productCode1 == "ACQ_V1_POS" ? "052-Acq POS level" : "052-Acq ECOM level",

    if (productCode === "ACQ_V1_POS") {

        if (deviceType.toUpperCase().includes("PAYCORE_TOP")) {
            return "052-Acq MPOS level";
        }
        return "052-Acq POS level";

    }

    return "052-Acq ECOM level";
}



// ==========================================
// STORE CLASSIFIERS
// ==========================================

const yesNo = (val?: string) =>
    val?.toUpperCase() === "Y";

const mapStoreClassifiers = (classifiers: any[] = []) => {
    const get = (code: string) =>
        classifiers.find(c => c.classifier === code)?.value;

    return {
        acqBrandAmexSupport: yesNo(get("ENABLE_AMEX")),
        acqBrandClaveValeSupport: yesNo(get("ENABLE_CLVL")),
        acqBrandMastercardSupport: yesNo(get("ENABLE_MC")),
        acqBrandVisaSupport: yesNo(get("ENABLE_VISA")),
        corporateStatus:
            get("MWF_CSTAT") === "0"
                ? "NOT_SOLE_PROPRIETOR"
                : "SOLE_PROPRIETOR",
        visaAmmfMerchantRegistration:
            yesNo(get("ENABLE_VISA_AMMF_REG")),
        amexAmmfMerchantRegistration:
            yesNo(get("ENABLE_AMEX_DEMO_REG"))
    };
};

const mapStoreLimits = (limits: any[] = []) => {
    return limits.map(l => ({
        tariffCode: l.tariffCode,
        dateFrom: l.dateFrom ?? "",
        currency: l.currency,
        maxAmount: l.maxAmount ?? "",
        maxSingleAmount: "",
        maxNumber: ""
    }));
};

const mapDeviceLimits = (limits: any[] = []) => {
    return limits.map(l => ({
        tariffCode: l.tariffCode,
        dateFrom: l.dateFrom ?? "",
        currency: l.currency,
        maxAmount: l.maxAmount ?? "",
        maxSingleAmount: l.maxSingleAmount ?? "",
        maxNumber: l.maxNumber ?? ""
    }));
};

const mapDeviceTariffs = (tariffs: any[] = []) => {
    return tariffs.map(t => ({
        tariffCode: t.tariffCode,
        dateFrom: t.dateFrom ?? "",
        feeBase: "0",
        feeMin: "0",
        feeMax: "0",
        feePcnt: t.feePcnt ?? "0"
    }));
};


// ==========================================
// DEVICE CLASSIFIERS
// ==========================================

const mapDeviceClassifiers = (classifiers: any[] = []) =>
    classifiers.map(c => ({
        code: c.classifier,
        value: c.value,
        isDefault: c.classifier !== "POS_TIP"
    }));

// ==========================================
// DEVICE PARAMETERS
// ==========================================

const mapDeviceParams = (params: any[] = []) => {
    const get = (code: string) =>
        params.find(p => p.code === code)?.value;

    return {
        egwDisable: get("EGW_DISABLE"),
        egwTermGroup: get("EGW_TERM_GROUP"),
        terminalTimeZone: get("TDS_GMT"),
        teleredId: get("TELERED_ID"),
        egwPaymentFacilitator: "N"
    };
};

// ==========================================
// SETTLEMENT PARAMETERS
// ==========================================

const mapSettlementParams = (params: any[] = []) => {
    const get = (code: string) =>
        params.find(p => p.code === code)?.value;

    return {
        localCurrencyAccountNumber: get("TGT_NUM_LOCAL"),
        usdAccountNumber: get("TGT_NUM_USD"),
        tariffPlan: get("FEE_T_PLAN"),

    };
};

// ==========================================
// ADDRESS MAPPERS
// ==========================================

const mapRegistrationAddress = (client: any) => ({
    addressType: "REGISTRATION_ADDRESS",
    countryId: client.country == 'PAN' ? 166 : 53,
    country: client.country == 'PAN' ? 166 : 53,
    provinceId: parseInt(client.address.state),
    cantonCode: client.address.cantonCode ?? "",
    districtCode: client.address.districtCode ?? "",
    provinceName: "",
    cantonName: client.address.city,
    districtName: client.address.district,
    addressLine1: client.address.line1 + " ",
    addressLine2: client.address.line2,
    contact: {
        firstName: client.firstName,
        lastName: client.lastName,
        phone: client.phone,
        mobilePhone: client.mobilePhone,
        email: client.address.email
    },
    postalCode: client.address.zipCode,
    firstName: client.firstName,
    lastName: client.lastName,
    phone: client.phone,
    mobilePhone: client.mobilePhone,
    email: client.address.email
});

const mapStoreAddress = (store: any) => ({
    addressType: "PAYMENT_SCHEME_ADDRESS",
    countryId: store.address.country == 'PAN' ? 166 : 53,
    country: store.address.country == 'PAN' ? 166 : 53,
    provinceId: parseInt(store.address.state),
    cantonCode: store.address.cantonCode ?? '', // ONB_CAT_MUNICIPALITY id
    districtCode: store.address.districtCode ?? '', // ONB_CAT_CITIES id
    provinceName: "",
    cantonName: store.address.city,
    districtName: store.address.district,
    addressLine1: "",
    addressLine2: "",
    merchantName: store.address.line1 ?? store.contractName,
    location: `${store.address.line2} ${store.address.line3 ?? ""}`,
    contact: {
        firstName: store.address.firstName,
        lastName: store.address.lastName,
        phone: store.address.phone,
        mobilePhone: store.address.mobilePhone,
        email: store.address.email
    },
    postalCode: store.address.zipCode,
    firstName: store.address.firstName,
    lastName: store.address.lastName,
    phone: store.address.phone,
    mobilePhone: store.address.mobilePhone,
    email: store.address.email
});

// ==========================================
// UTIL
// ==========================================

const generateId = () => uuidv4();
